import { isBefore, isSameDay } from "date-fns";
import { useState } from "react";
import useAsyncEffect from "use-async-effect";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import { getPendingInvoiceStatusReport } from "~/modules/billing/api/pendingInvoice/pendingInvoiceApiDispatchers.ts";
import type { PendingInvoiceStatusReportData } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	invoiceId: string;
	deliveryStartDate: Date;
	deliveryEndDate: Date;
	originalStartDate: Date;
	originalEndDate: Date;
	onPreCheckComplete?: (data: PendingInvoiceStatusReportData) => void;
};

type UsePendingInvoicePreCheckResult = {
	preCheckData: PendingInvoiceStatusReportData | null;
	isPerformingPreCheck: boolean;
	hasConflicts: boolean;
	invoiceTotalIsInvalid: boolean;
};

const useInvoiceDetailsByDeliveryPeriod = ({
	invoiceId,
	deliveryStartDate,
	deliveryEndDate,
	originalStartDate,
	originalEndDate,
	onPreCheckComplete,
}: Props): UsePendingInvoicePreCheckResult => {
	const [isPerformingPreCheck, setIsPerformingPreCheck] = useState<boolean>(false);
	const [preCheckData, setPreCheckData] = useState<PendingInvoiceStatusReportData | null>(null);

	const dateChanged =
		!isSameDay(deliveryStartDate, originalStartDate) ||
		!isSameDay(deliveryEndDate, originalEndDate);

	useAsyncEffect(
		async (isMounted) => {
			if (!dateChanged) {
				setPreCheckData(null);
				setIsPerformingPreCheck(false);
				return;
			}
			if (isBefore(deliveryEndDate, deliveryStartDate)) return;

			setIsPerformingPreCheck(true);
			const startDate = formatDateToYYYYMMDD(deliveryStartDate);
			const endDate = formatDateToYYYYMMDD(deliveryEndDate);
			const preCheckResponse = await getPendingInvoiceStatusReport(invoiceId, startDate, endDate);
			if (!isMounted()) return;
			const data = getDataFromResponse(preCheckResponse);
			setPreCheckData(data);
			onPreCheckComplete?.(data);
			setIsPerformingPreCheck(false);
		},
		[dateChanged, deliveryEndDate, deliveryStartDate],
	);

	const hasConflicts = (preCheckData?.conflicts?.length ?? 0) > 0;
	const invoiceTotalIsInvalid = preCheckData?.subtotalCents === 0;

	return {
		preCheckData,
		isPerformingPreCheck,
		hasConflicts,
		invoiceTotalIsInvalid,
	};
};

export default useInvoiceDetailsByDeliveryPeriod;